/**
 * Shared copy + pill-tone helpers for the ADR-0129 §2.5 dashboard
 * surfaces (Settings → Skills + Visibility → Skills health panel).
 *
 * Founder's framing (ADR-0129 §2.6): Sill surfaces TRUTH; the
 * dashboard never auto-toggles. These helpers turn the route's
 * `SkillStatus` + `GatingReason` enums into the precise low-marketing
 * copy the merchant reads — once, in one place, so Settings and
 * Visibility never drift.
 *
 * Both functions are exhaustive switches over the closed enums
 * (`packages/types/src/skill.ts`). Adding a new `SkillStatus` or
 * `GatingReason` value is a typecheck error here — the structural
 * mitigation against the same drift bug ADR-0129 was written to
 * foreclose (§2.1 single-source-of-truth invariant).
 *
 * Voice (CLAUDE.md §8): precise, technical, low-marketing. No
 * exclamation points, no emoji, no vague verbs. Match the existing
 * dashboard tone.
 */

import type {
  GatingReason,
  SkillFulfillmentPath,
  SkillStatus,
} from '@sill/types';

import type { PillTone } from '../components/atoms/Pill.js';

/**
 * Visual + label mapping for the per-skill pill. Returns the tone +
 * the short label the row renders. The label is intentionally short
 * (single uppercase word or short phrase) to fit the dashboard's
 * mono-pill density (Pill.tsx renders 9px JetBrains Mono).
 *
 * Tone direction is from ADR-0129 §2.5:
 *   - live              → lime  (approve / on-card)
 *   - enabled_unbacked  → amber (escalate / merchant can resolve)
 *   - enabled_unsupported → neutral (Coming soon — not the merchant's
 *                          fault; we don't ship it yet)
 *   - tier_gated        → sky   (pending / verify-domain)
 *   - disabled          → coral (merchant chose off; matches the
 *                          existing pre-0129 DISABLED pill exactly so
 *                          the rebrand is invisible on the toggle-off
 *                          path)
 */
export function skillStatusToPill(
  status: SkillStatus,
): { tone: PillTone; label: string } {
  switch (status) {
    case 'live':
      return { tone: 'lime', label: 'LIVE' };
    case 'enabled_unbacked':
      return { tone: 'amber', label: 'NOT LIVE' };
    case 'enabled_unsupported':
      return { tone: 'neutral', label: 'COMING SOON' };
    case 'tier_gated':
      return { tone: 'sky', label: 'VERIFY DOMAIN' };
    case 'disabled':
      return { tone: 'coral', label: 'DISABLED' };
  }
}

/**
 * Map a `GatingReason` to the one-line next-step hint the row
 * renders under its pill. The mapping is total over the closed enum
 * — the switch's exhaustiveness is what makes the helper a
 * single-source-of-truth (per the implementer handoff's "in ONE
 * place" requirement; ADR-0129 §2.1's structural drift mitigation).
 *
 * Copy is deliberately concrete — every reason names the action the
 * merchant takes ("connect a payment rail (Stripe) to go live")
 * rather than the internal state ("`has_refund_rail = false`"). The
 * `below_tier_floor` arm carries no internal tier algebra (ADR-0129
 * §2.5: "don't expose internal tier algebra" — the handoff direction
 * is "Verify your domain to enable").
 */
export function gatingReasonToNextStep(reason: GatingReason): string {
  switch (reason) {
    case 'no_refund_rail':
      return 'connect a payment rail (Stripe) to go live';
    case 'no_checkout_eligible_catalog':
      return 'no product passes your checkout rules — review Catalog eligibility to go live';
    case 'missing_compliance_fields':
      return 'add your ToS, privacy, and returns policies (Catalog → Compliance) to go live';
    case 'no_checkout_rule':
      return 'add a checkout catalog rule and activate eligibility (Catalog) to go live';
    case 'no_search_eligible_catalog':
      return 'add catalog products to go live';
    case 'no_fulfillment_connector':
      return 'connect a fulfillment backend to go live';
    case 'no_adapter':
      return 'not yet supported';
    case 'endpoint_unreachable':
      return 'fulfillment endpoint unreachable — check the URL and re-save';
    case 'below_tier_floor':
      return 'verify your domain to enable';
  }
}

/**
 * Compose the full hint text rendered under the pill for a given
 * status + reason. Centralizes the "Enabled — " / "Coming soon — "
 * prefix logic that would otherwise drift between Settings and
 * Visibility. Returns `null` when there's nothing to say (a `live`
 * row reads "Live to agents." via its own surface; a `disabled` row
 * is mute by design — the toggle IS the message).
 */
export function skillHintText(
  status: SkillStatus,
  gating_reason: GatingReason | undefined,
): string | null {
  switch (status) {
    case 'live':
      return 'Live to agents.';
    case 'enabled_unbacked': {
      // The route guarantees `gating_reason` is present on this
      // status (ADR-0129 §2.4). Defensive fallback for a stale wire.
      const step = gating_reason !== undefined
        ? gatingReasonToNextStep(gating_reason)
        : 'not yet live to agents';
      return `Enabled — ${step}.`;
    }
    case 'enabled_unsupported':
      // `no_fulfillment_connector` is merchant-actionable (connect Shopify
      // or a custom endpoint); the other unsupported reasons reflect a Sill
      // capability gap, not a merchant configuration gap.
      if (gating_reason === 'no_fulfillment_connector') {
        return 'Enabled — connect a fulfillment backend to go live.';
      }
      return 'Coming soon — not yet supported.';
    case 'tier_gated':
      return 'Verify your domain to enable.';
    case 'disabled':
      return null;
  }
}

/**
 * Coarse bucket a `GatingReason` falls into, for surfaces that want to
 * summarize several unbacked skills as ONE sentence ("2 skills need a
 * catalog") rather than repeat a near-duplicate line per skill (the
 * `SitesList` setup-guide "skills need setup" nudge — see
 * `SkillsGapNudge.tsx`). Presentation-layer grouping only; the
 * underlying `GatingReason` enum and `gatingReasonToNextStep` are
 * unchanged. Exhaustive switch — same drift-mitigation shape as the
 * rest of this file.
 */
export type GatingReasonCategory = 'connector' | 'rail' | 'catalog' | 'tier';

export function gatingReasonCategory(reason: GatingReason): GatingReasonCategory {
  switch (reason) {
    case 'no_fulfillment_connector':
    case 'endpoint_unreachable':
    case 'no_adapter':
      return 'connector';
    case 'no_refund_rail':
      return 'rail';
    case 'no_search_eligible_catalog':
    case 'no_checkout_eligible_catalog':
    case 'no_checkout_rule':
    case 'missing_compliance_fields':
      return 'catalog';
    case 'below_tier_floor':
      return 'tier';
  }
}

/** Short action phrase for a category summary line — see `gatingReasonCategory`. */
export function gatingReasonCategoryLabel(category: GatingReasonCategory): string {
  switch (category) {
    case 'connector':
      return 'connect a store or fulfillment endpoint';
    case 'rail':
      return 'connect a payment rail';
    case 'catalog':
      return 'finish your catalog setup';
    case 'tier':
      return 'verify your domain';
  }
}

/**
 * ADR-0174 §8 — the small "handoff" kicker rendered below `place_order`'s
 * status hint when the skill is live via the WooCommerce payment-link
 * handoff route (§3 third branch). Names the fulfillment route without
 * asserting anything about the merchant's checkout — the buyer will
 * complete payment on the merchant's own site under the merchant's
 * gateway. Returns `null` for `'settlement'` (that's the default /
 * unmarked state — settlement is what today's `place_order` LIVE
 * already means; no new copy needed) and for absent (any skill_id other
 * than `place_order`, or a non-live `place_order`).
 *
 * PLACEHOLDER COPY — marketing owns final wording per ADR-0174 §7
 * ("final copy through the `marketing` subagent post-build") + the
 * positioning skill's verified-facts discipline. Do not treat this
 * string as final. Voice-consistent short-form until then.
 */
export function skillFulfillmentPathKicker(
  fulfillment_path: SkillFulfillmentPath | undefined,
): string | null {
  if (fulfillment_path === undefined) return null;
  switch (fulfillment_path) {
    case 'settlement':
      // The default place_order lit-by-Sill copy is unchanged; no
      // extra line. Emitting `null` here leaves the row's rendering
      // byte-identical to today's for settlement-lit merchants.
      return null;
    case 'handoff':
      return 'Handoff — buyer completes checkout on your own site.';
  }
}
